import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap, ArrowRight, Github, Twitter, Linkedin } from "lucide-react";

const NAV_LINKS = [
  { to: "/features", label: "Features" },
  { to: "/pricing",  label: "Pricing" },
  { to: "/about",    label: "About" },
  { to: "/contact",  label: "Contact" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group">
      <div className="relative w-8 h-8">
        <div className="absolute inset-0 rounded-lg bg-ay-cyan/20 group-hover:bg-ay-cyan/30 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Zap size={16} className="text-ay-cyan" />
        </div>
        <div className="absolute -inset-0.5 rounded-lg border border-ay-cyan/30 group-hover:border-ay-cyan/60 transition-colors" />
      </div>
      <span className="font-bold text-lg tracking-tight">
        <span className="text-white">Alpha</span>
        <span className="text-ay-cyan">You</span>
        <span className="text-ay-muted font-normal text-sm ml-1">AI</span>
      </span>
    </Link>
  );
}

export default function MarketingLayout() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-ay-bg text-white">
      {/* ── Top announcement bar ── */}
      <div className="bg-ay-cyan/10 border-b border-ay-cyan/20 py-2 px-4 text-center text-xs text-ay-cyan font-medium">
        <span className="opacity-70">🇬🇧</span>
        {" "}UK data residency · NICE-aligned · GDPR compliant · Now in early access
        <Link to="/contact" className="ml-3 underline underline-offset-2 hover:text-white transition-colors">
          Request access →
        </Link>
      </div>

      {/* ── Navigation ── */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-ay-bg/90 backdrop-blur-xl border-b border-ay-border shadow-ay-sm"
            : "bg-transparent"
        }`}
      >
        <nav className="container-ay flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <Logo />

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link px-4 py-2 rounded-lg hover:bg-white/5 ${
                  pathname === link.to ? "text-white" : ""
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/app" className="btn-ghost text-sm">
              Sign in
            </Link>
            <Link to="/app" className="btn-primary text-sm py-2 px-5">
              Get started <ArrowRight size={14} />
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-ay-muted hover:text-white hover:bg-white/5 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </nav>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-ay-border bg-ay-bg/95 backdrop-blur-xl"
            >
              <div className="px-4 py-4 space-y-1">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block px-4 py-3 rounded-lg text-sm font-medium text-ay-muted hover:text-white hover:bg-white/5 transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-3 border-t border-ay-border flex flex-col gap-2">
                  <Link to="/app" className="btn-outline text-sm justify-center">Sign in</Link>
                  <Link to="/app" className="btn-primary text-sm justify-center">Get started</Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── Page content ── */}
      <main>
        <Outlet />
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-ay-border bg-ay-surface/50">
        <div className="container-ay px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2">
              <Logo />
              <p className="mt-4 text-sm text-ay-muted leading-relaxed max-w-xs">
                The UK's most advanced clinical intelligence platform. Built on NICE guidelines,
                BNF data, and NHS pathways — not US standards.
              </p>
              <div className="flex gap-3 mt-5">
                {[
                  { icon: Twitter, href: "#", label: "Twitter" },
                  { icon: Linkedin, href: "#", label: "LinkedIn" },
                  { icon: Github, href: "#", label: "GitHub" },
                ].map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    className="w-9 h-9 rounded-lg border border-ay-border flex items-center justify-center text-ay-muted hover:text-white hover:border-ay-cyan/40 transition-all"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>

            {/* Links */}
            {[
              {
                title: "Product",
                links: [
                  { label: "Features", to: "/features" },
                  { label: "Pricing", to: "/pricing" },
                  { label: "Changelog", to: "#" },
                  { label: "Roadmap", to: "#" },
                ],
              },
              {
                title: "Company",
                links: [
                  { label: "About", to: "/about" },
                  { label: "Contact", to: "/contact" },
                  { label: "Careers", to: "#" },
                  { label: "Blog", to: "#" },
                ],
              },
              {
                title: "Legal",
                links: [
                  { label: "Privacy Policy", to: "#" },
                  { label: "Terms of Service", to: "#" },
                  { label: "GDPR", to: "#" },
                  { label: "Security", to: "#" },
                ],
              },
            ].map((col) => (
              <div key={col.title}>
                <h4 className="text-xs font-semibold text-white uppercase tracking-widest mb-4">
                  {col.title}
                </h4>
                <ul className="space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="text-sm text-ay-muted hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="ay-divider pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-ay-muted">
              © {new Date().getFullYear()} AlphaYou Ltd. All rights reserved. Registered in England & Wales.
            </p>
            <div className="flex items-center gap-4 text-xs text-ay-muted">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-ay-emerald animate-pulse-slow" />
                All systems operational
              </span>
              <span>eu-west-2 · London</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
